// SA-3
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { pb } from '../../../pocketbase'
import { COL_MACHINES, COL_PRODUCTION_WORKERS, COL_SHIFTS } from '../../../config/collections'
import type { MachineRecord, ProductionWorkerRecord, ShiftRecord } from '../../../types/records'
import { formatMachineLabel } from '../../../lib/machines'
import {
  PRODUCTION_POSITION_OPTIONS,
  isPositionShift5_2,
} from '../../../lib/productionWorkers'
import { Alert, ButtonLink, FormField, PageHeader, PageShell, formInputProps } from '../../../components/ui'
import { btn, cn, formActionsClass, formGridClass } from '../../../lib/ui'
import { pocketbaseErrorMessage } from '../../../lib/pocketbaseErrorMessage'

export function ProductionWorkerFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = id === 'new' || !id

  const [shifts, setShifts] = useState<ShiftRecord[]>([])
  const [machines, setMachines] = useState<MachineRecord[]>([])
  const [name, setName] = useState('')
  const [position, setPosition] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [defaultShift, setDefaultShift] = useState('')
  const [defaultMachine, setDefaultMachine] = useState('')
  const [active, setActive] = useState(true)
  const [notes, setNotes] = useState('')
  const [hourlyRate, setHourlyRate] = useState('')
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(!isNew)

  const loadShifts = useCallback(async () => {
    try {
      const list = await pb.collection(COL_SHIFTS).getFullList<ShiftRecord>({
        sort: 'sort,name',
        filter: 'active = true',
      })
      setShifts(list)
    } catch {
      setShifts([])
    }
  }, [])

  const loadMachines = useCallback(async () => {
    try {
      const list = await pb.collection(COL_MACHINES).getFullList<MachineRecord>({
        sort: 'type,sort,number',
      })
      setMachines(list)
    } catch {
      setMachines([])
    }
  }, [])

  const load = useCallback(async () => {
    if (!id || isNew) return
    setLoading(true)
    setErr('')
    try {
      const row = await pb.collection(COL_PRODUCTION_WORKERS).getOne<ProductionWorkerRecord>(id)
      setName(row.name)
      setPosition(row.position || '')
      setPhone(row.phone || '')
      setEmail(row.email || '')
      setDefaultShift(row.defaultShift || '')
      setDefaultMachine(row.defaultMachine || '')
      setActive(row.active)
      setNotes(row.notes || '')
      setHourlyRate(row.hourlyRate != null ? String(row.hourlyRate) : '')
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка'))
    } finally {
      setLoading(false)
    }
  }, [id, isNew])

  useEffect(() => {
    void loadShifts()
    void loadMachines()
  }, [loadShifts, loadMachines])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!isPositionShift5_2(position)) return
    const shift52 = shifts.find((s) => s.name === 'Смена 5/2')
    if (shift52) setDefaultShift(shift52.id)
  }, [position, shifts])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErr('')

    const nameVal = name.trim()
    if (!nameVal) {
      setErr('Укажите ФИО сотрудника.')
      return
    }

    let hourlyRateVal: number | undefined
    if (hourlyRate.trim()) {
      hourlyRateVal = Number(String(hourlyRate).replace(',', '.'))
      if (!Number.isFinite(hourlyRateVal) || hourlyRateVal < 0) {
        setErr('Почасовая ставка — неотрицательное число.')
        return
      }
    }

    const body: Record<string, unknown> = {
      name: nameVal,
      position: position.trim() || '',
      phone: phone.trim() || '',
      email: email.trim() || '',
      defaultShift: defaultShift || '',
      defaultMachine: defaultMachine || '',
      active,
      notes: notes.trim() || '',
    }
    if (hourlyRateVal != null) {
      body.hourlyRate = hourlyRateVal
    } else if (!isNew) {
      body.hourlyRate = null
    }

    try {
      if (isNew) {
        await pb.collection(COL_PRODUCTION_WORKERS).create(body)
      } else {
        await pb.collection(COL_PRODUCTION_WORKERS).update(id!, body)
      }
      navigate('/admin/administration/production-workers', { replace: true })
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка сохранения'))
    }
  }

  return (
    <PageShell>
      <PageHeader
        title={isNew ? 'Новый сотрудник производства' : 'Редактирование сотрудника'}
        lead="Сотрудник используется в табеле и на экране смены; вход в систему не требуется."
      />
      {err && <Alert kind="err">{err}</Alert>}
      {loading && !isNew ? (
        <p>Загрузка…</p>
      ) : (
        <form onSubmit={(e) => void onSubmit(e)} className={cn(formGridClass, 'max-w-[40rem]')}>
          <FormField label="ФИО" htmlFor="pw-name">
            <input
              id="pw-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={200}
              {...formInputProps()}
            />
          </FormField>
          <FormField label="Должность" htmlFor="pw-position">
            <input
              id="pw-position"
              type="text"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              list="pw-position-options"
              maxLength={120}
              {...formInputProps()}
            />
            <datalist id="pw-position-options">
              {PRODUCTION_POSITION_OPTIONS.map((opt) => (
                <option key={opt} value={opt} />
              ))}
            </datalist>
          </FormField>
          <FormField label="Телефон" htmlFor="pw-phone">
            <input
              id="pw-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              maxLength={30}
              placeholder="+7 (___) ___-__-__"
              {...formInputProps()}
            />
          </FormField>
          <FormField label="Email" htmlFor="pw-email">
            <input
              id="pw-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Для связи или входа в СУП"
              {...formInputProps()}
            />
          </FormField>
          <FormField label="Смена по умолчанию" htmlFor="pw-shift">
            <select
              id="pw-shift"
              value={defaultShift}
              onChange={(e) => setDefaultShift(e.target.value)}
              {...formInputProps()}
            >
              <option value="">— не задана —</option>
              {shifts.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.startTime}–{s.endTime})
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Станок по умолчанию" htmlFor="pw-machine">
            <select
              id="pw-machine"
              value={defaultMachine}
              onChange={(e) => setDefaultMachine(e.target.value)}
              {...formInputProps()}
            >
              <option value="">— не задан —</option>
              {machines.map((m) => (
                <option key={m.id} value={m.id}>
                  {formatMachineLabel(m)}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Почасовая ставка, ₽" htmlFor="pw-rate">
            <input
              id="pw-rate"
              type="number"
              min={0}
              step="any"
              value={hourlyRate}
              onChange={(e) => setHourlyRate(e.target.value)}
              placeholder="Необязательно"
              {...formInputProps()}
            />
          </FormField>
          <FormField label="Активен" htmlFor="pw-active">
            <label className="flex items-center gap-2">
              <input
                id="pw-active"
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
              <span>Участвует в табеле и планировании</span>
            </label>
          </FormField>
          <FormField label="Примечания" htmlFor="pw-notes">
            <textarea
              id="pw-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={4000}
              className={formInputProps().className}
            />
          </FormField>
          <div className={formActionsClass}>
            <button type="submit" className={btn('primary')}>
              Сохранить
            </button>
            <ButtonLink to="/admin/administration/production-workers" variant="ghost">
              Отмена
            </ButtonLink>
          </div>
        </form>
      )}
    </PageShell>
  )
}
